import { FC, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import dayjs from "dayjs";
import { CustomButton } from "../../atoms/button/button";
import { Colors, GET_PINTAG_BOOKS, GET_PINTAG_BOXES, RELEASE_PINTAG_BOOK } from "../../../configs";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { BaseResponse } from "../../../typings/interfaces";
import { IPintagBook, IPintagBoxBooks, IPintagBoxStatus } from "../../../typings/interfaces/pos/pintag";
import { bookLabel, colourForDay } from "../../../util/pintag-books";
import { storeNow } from "../../../util/store-clock";

export interface IPinTagBooksProps {
  /** Manager who approved access with their PIN; recorded as the releaser */
  releasedByUserId?: number;
}

// "Red · Monday"; just "Monday" when the colour is named after its day
const colourName = (c: IPintagBoxStatus) => {
  const name = c.description || c.alias;
  const day = c.day ? `${c.day.charAt(0)}${c.day.slice(1).toLowerCase()}` : "";
  return day && name.toLowerCase() !== day.toLowerCase() ? `${name} · ${day}` : name;
};

const STATUS_STYLE: Record<IPintagBook["status"], { label: string; bg: string; border: string; text: string }> = {
  RELEASED: { label: "Released", bg: "#E8F5EE", border: "#BFEAD3", text: "#0F6B43" },
  NOT_RELEASED: { label: "In box", bg: "#FFFFFF", border: "#D1D5DB", text: "#4B5563" },
  DISABLED: { label: "Disabled", bg: "#F3F4F6", border: "#E5E7EB", text: "#9CA3AF" },
};

/**
 * A pin tag colour's open boxes with their books of 50 tags (000–049 …
 * 950–999). Tags can be used only from released books; releasing a book
 * disables the same numbers in older boxes. Opens on today's colour.
 */
export const PinTagBooks: FC<IPinTagBooksProps> = ({ releasedByUserId }) => {
  const snackbar = useSnackbarContext();
  const [colours, setColours] = useState<IPintagBoxStatus[]>([]);
  const [colourId, setColourId] = useState<number | null>(null);
  const [boxes, setBoxes] = useState<IPintagBoxBooks[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState<{ box: IPintagBoxBooks; book: IPintagBook } | null>(null);
  const [releasing, setReleasing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get<BaseResponse<IPintagBoxStatus>>(GET_PINTAG_BOXES);
        if (res.data?.status !== "SUCCESS") throw new Error(res.data?.message || "Could not load pin tag colours");
        const list = res.data.body ?? [];
        setColours(list);
        setColourId(colourForDay(list, storeNow())?.pintagColorId ?? null);
      } catch (err: any) {
        snackbar.showSnackbar(err?.message || "Could not load pin tag colours", "error");
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (colourId == null) return;
    setLoading(true);
    try {
      const res = await axios.get<BaseResponse<IPintagBoxBooks>>(GET_PINTAG_BOOKS(colourId));
      if (res.data?.status !== "SUCCESS") throw new Error(res.data?.message || "Could not load pin tag books");
      setBoxes(res.data.body ?? []);
    } catch (err: any) {
      snackbar.showSnackbar(err?.message || "Could not load pin tag books", "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colourId]);

  useEffect(() => {
    load();
  }, [load]);

  const release = async () => {
    if (!confirm) return;
    setReleasing(true);
    try {
      const res = await axios.post<BaseResponse<IPintagBoxBooks>>(RELEASE_PINTAG_BOOK(confirm.book.id), {
        releasedByUserId,
      });
      if (res.data?.status !== "SUCCESS") throw new Error(res.data?.message || "Could not release the book");
      setBoxes(res.data.body ?? []);
      snackbar.showSnackbar(
        `Box ${confirm.box.boxNumber}, book ${bookLabel(confirm.book.startNumber, confirm.book.endNumber)} released`,
        "success"
      );
      setConfirm(null);
    } catch (err: any) {
      snackbar.showSnackbar(err?.message || "Could not release the book", "error");
    } finally {
      setReleasing(false);
    }
  };

  const colour = colours.find((c) => c.pintagColorId === colourId);
  const olderWithSameRange = (book: IPintagBook, box: IPintagBoxBooks) =>
    boxes.filter(
      (other) =>
        other.boxNumber < box.boxNumber &&
        other.books.some((b) => b.startNumber === book.startNumber && b.status !== "DISABLED")
    );

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Colour (day) picker */}
      <Box role="tablist" aria-label="Pin tag colour" sx={{ display: "flex", gap: "0.8rem", flexWrap: "wrap" }}>
        {colours.map((c) => {
          const selected = c.pintagColorId === colourId;
          return (
            <Box
              key={c.pintagColorId}
              component="button"
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setColourId(c.pintagColorId)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                px: "1.2rem",
                py: "0.6rem",
                borderRadius: "99px",
                border: `1.5px solid ${selected ? Colors.PRIMARY : "#D1D5DB"}`,
                backgroundColor: selected ? `${Colors.PRIMARY}14` : Colors.WHITE,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: "var(--fs-base)",
                fontWeight: selected ? 700 : 500,
                color: Colors.TEXT_GRAY_DARK,
              }}
            >
              <Box sx={{ width: "1.4rem", height: "1.4rem", borderRadius: "50%", background: c.color, border: "1px solid #0002" }} />
              {colourName(c)}
            </Box>
          );
        })}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: "3rem" }}>
          <CircularProgress />
        </Box>
      ) : (
        boxes.map((box) => {
          const released = box.books.filter((b) => b.status === "RELEASED");
          const left = released.reduce((sum, b) => sum + b.remaining, 0);
          return (
            <Box
              key={box.boxId}
              component="section"
              aria-label={`Box ${box.boxNumber}`}
              data-testid={`pintag-box-${box.boxNumber}`}
              sx={{ border: "1px solid #E5E7EB", borderRadius: "1rem", p: "1.4rem", backgroundColor: "#FAFAFA" }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: "1rem", gap: "1rem", flexWrap: "wrap" }}>
                <Typography component="h2" sx={{ fontSize: "var(--fs-lg)", fontWeight: 700, color: Colors.TEXT_GRAY_DARK }}>
                  {colour?.description || colour?.alias} · Box {box.boxNumber}
                  {box.current ? "" : " (older box)"}
                </Typography>
                <Typography sx={{ fontSize: "var(--fs-xs)", color: Colors.TEXT_GRAY_DARK }}>
                  {box.openedAt ? `Opened ${dayjs(box.openedAt).format("MMM D, YYYY")}` : ""}
                  {box.openedBy ? ` by ${box.openedBy}` : ""} · {released.length} of 20 books released · {left} tags left
                  in released books
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(13rem, 1fr))", gap: "0.8rem" }}>
                {box.books.map((book) => {
                  const style = STATUS_STYLE[book.status];
                  const label = bookLabel(book.startNumber, book.endNumber);
                  return (
                    <Box
                      key={book.id}
                      data-testid={`book-${box.boxNumber}-${book.startNumber}`}
                      data-status={book.status}
                      sx={{
                        border: `1px solid ${style.border}`,
                        backgroundColor: style.bg,
                        borderRadius: "0.8rem",
                        p: "0.8rem 1rem",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.3rem",
                      }}
                    >
                      <Typography sx={{ fontSize: "var(--fs-md)", fontWeight: 700, color: style.text, fontFamily: "var(--font-mono)" }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 600, color: style.text }}>{style.label}</Typography>
                      <Typography sx={{ fontSize: "var(--fs-2xs)", color: Colors.TEXT_GRAY_DARK }}>
                        {book.status === "NOT_RELEASED"
                          ? "50 tags"
                          : `${book.used} used · ${book.status === "DISABLED" ? "rest unusable" : `${book.remaining} left`}`}
                      </Typography>
                      {book.status === "NOT_RELEASED" ? (
                        <CustomButton
                          sx={{ mt: "0.3rem", fontSize: "var(--fs-2xs)", py: "0.4rem" }}
                          onClick={() => setConfirm({ box, book })}
                          aria-label={`Release book ${label} of box ${box.boxNumber}`}
                        >
                          Release
                        </CustomButton>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })
      )}

      <Dialog open={!!confirm} onClose={() => !releasing && setConfirm(null)}>
        <DialogTitle sx={{ fontSize: "var(--fs-lg)" }}>Release this book?</DialogTitle>
        <DialogContent>
          {confirm && (
            <Typography sx={{ fontSize: "var(--fs-base)" }}>
              Box {confirm.box.boxNumber}, tags {bookLabel(confirm.book.startNumber, confirm.book.endNumber)} can be used
              at the counter.
              {olderWithSameRange(confirm.book, confirm.box).length > 0
                ? ` The same numbers left in Box ${olderWithSameRange(confirm.book, confirm.box)
                    .map((b) => b.boxNumber)
                    .join(", ")} will be disabled and can't be used any more.`
                : ""}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: "2rem", pb: "1.5rem", gap: "1rem" }}>
          <CustomButton
            sx={{ px: "2rem", fontSize: "var(--fs-base)", background: Colors.MAIN_GRAY, color: Colors.WHITE }}
            onClick={() => setConfirm(null)}
            disabled={releasing}
          >
            Cancel
          </CustomButton>
          <CustomButton sx={{ px: "2rem", fontSize: "var(--fs-base)" }} onClick={release} disabled={releasing}>
            {releasing ? <CircularProgress size="1.6rem" /> : "Release Book"}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
